import { useState, useEffect, useCallback } from "react";
import altusLogoBrand from "@/assets/logo-altus-brand.png";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Grid3X3, 
  CreditCard, 
  ShoppingBag, 
  Truck, 
  AlertTriangle,
  Users,
  Bell,
  CheckCircle2,
  Circle,
  ArrowDown,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useTableConsumptionSettings } from "@/hooks/useTableConsumptionSettings";
import { useTableConsumptionValues } from "@/hooks/useTableConsumptionValues";
import { useCommandEntryValidation } from "@/hooks/useCommandEntryValidation";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { useAuth } from "@/hooks/useAuth";
import { PeopleCountDialog } from "./PeopleCountDialog";
import { WaiterSessionsDialog } from "./WaiterSessionsDialog";
import { useWaiterSessionsManagement } from "@/hooks/useWaiterSessionsManagement";
import { Badge } from "@/components/ui/badge";
import { DeliveryOrderDialog, DeliveryCustomerData } from "./DeliveryOrderDialog";
import { useConnectivity } from "@/hooks/useConnectivity";

const normalizeNumber = (value: string): string => {
  return value.replace(/^0+/, '') || '0';
};

export type DestinationType = "table" | "tab" | "counter" | "delivery";

const VALID_MODES: DestinationType[] = ["table", "tab", "counter", "delivery"];

function getLastModeStorageKey(restaurantId?: string | null, userId?: string | null): string | null {
  if (!restaurantId) return null;
  return `pdv:lastMode:${restaurantId}:${userId ?? "anon"}`;
}

function readPersistedMode(key: string | null): DestinationType {
  if (!key || typeof window === "undefined") return "table";
  try {
    const v = window.localStorage.getItem(key);
    if (v && (VALID_MODES as string[]).includes(v)) return v as DestinationType;
  } catch {}
  return "table";
}

export interface DeliveryData {
  customerName: string;
  customerPhone: string;
  deliveryAddress: string;
  notes?: string;
}

export interface SelectedDestination {
  type: DestinationType;
  number: string;
  id?: string;
  customerName?: string;
  deliveryData?: DeliveryData;
}

interface PDVSelectionScreenProps {
  tables: Array<{ id: string; number: string; status?: string | null; name?: string | null; grouped_to_order_id?: string | null }>;
  commands: Array<{ id: string; number: string; status?: string | null; customer_name?: string | null; grouped_to_order_id?: string | null }>;
  onSelectDestination: (destination: SelectedDestination, peopleCount?: number) => void;
  onExit?: () => void;
  onViewOpenBills?: () => void;
  onViewDeliveryOrders?: () => void;
  onViewMap?: () => void;
  operatorName?: string;
  restaurantName?: string;
}

const modeConfig = [
  { type: "table" as const, label: "Mesa", shortcut: "F1", icon: Grid3X3 },
  { type: "tab" as const, label: "Comanda", shortcut: "F2", icon: CreditCard },
  { type: "counter" as const, label: "Balcão", shortcut: "F3", icon: ShoppingBag },
  { type: "delivery" as const, label: "Delivery", shortcut: "F4", icon: Truck },
];

function getInitials(name: string) {
  return name.split(" ").filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join("");
}



export function PDVSelectionScreen({
  tables,
  commands,
  onSelectDestination,
  onExit,
  onViewOpenBills,
  onViewDeliveryOrders,
  onViewMap,
  operatorName,
  restaurantName,
}: PDVSelectionScreenProps) {
  const { activeRestaurant } = useRestaurantContext();
  const { user } = useAuth();
  const storageKey = getLastModeStorageKey(activeRestaurant?.id, user?.id);
  const [selectedType, setSelectedType] = useState<DestinationType>(() => readPersistedMode(storageKey));
  const [inputValue, setInputValue] = useState("");
  const [pendingDestination, setPendingDestination] = useState<SelectedDestination | null>(null);
  const [showPeopleDialog, setShowPeopleDialog] = useState(false);
  const [showDeliveryDialog, setShowDeliveryDialog] = useState(false);
  
  const { showTableConsumptionValue, tableInactivityMinutes } = useTableConsumptionSettings();
  const { getTableConsumption, isTableInactive } = useTableConsumptionValues(tableInactivityMinutes);
  const { hasActiveEntry, getUsableCommands, requireEntry, getEntryForCommand } = useCommandEntryValidation();
  const { pendingRequests, activeWaiterSessions, activeTabletSessions } = useWaiterSessionsManagement();
  const [showWaiterDialog, setShowWaiterDialog] = useState(false);
  const { isOnline: isConnOnline } = useConnectivity();

  const usableCommands = getUsableCommands(commands);

  // Re-hydrate when restaurant/user changes (e.g. after auth resolves)
  useEffect(() => {
    if (!storageKey) return;
    const persisted = readPersistedMode(storageKey);
    setSelectedType((current) => (current === persisted ? current : persisted));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Persist mode changes
  useEffect(() => {
    if (!storageKey || typeof window === "undefined") return;
    try { window.localStorage.setItem(storageKey, selectedType); } catch {}
  }, [storageKey, selectedType]);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

  const handleKeyPress = (key: string) => {
    if (key === "backspace") setInputValue(prev => prev.slice(0, -1));
    else if (key === "clear") setInputValue("");
    else setInputValue(prev => prev + key);
  };

  const handleDeliveryConfirm = (data: DeliveryCustomerData) => {
    onSelectDestination({
      type: "delivery",
      number: "",
      customerName: data.customerName,
      deliveryData: data,
    });
  };

  const handleConfirm = useCallback(() => {
    if (selectedType === "counter") {
      onSelectDestination({ type: "counter", number: "" });
      return;
    }
    if (selectedType === "delivery") {
      setShowDeliveryDialog(true);
      return;
    }
    if (!inputValue) return;

    if (selectedType === "table") {
      const normalizedInput = normalizeNumber(inputValue);
      const table = tables.find(t => normalizeNumber(t.number) === normalizedInput);
      if (!table) {
        toast.error(`Mesa ${inputValue} não cadastrada`, { description: "Cadastre a mesa antes de lançar pedidos." });
        return;
      }
      const dest: SelectedDestination = { type: "table", number: table.number, id: table.id };
      if (table.status !== "occupied") {
        setPendingDestination(dest);
        setShowPeopleDialog(true);
      } else {
        onSelectDestination(dest);
      }
    } else if (selectedType === "tab") {
      const normalizedInput = normalizeNumber(inputValue);
      const command = commands.find(c => normalizeNumber(c.number) === normalizedInput);
      if (!command) {
        toast.error(`Comanda ${inputValue} não cadastrada`, { description: "Cadastre a comanda antes de lançar pedidos." });
        return;
      }
      if (requireEntry && !hasActiveEntry(command.number) && command.status !== "in_use") {
        toast.error(`Comanda ${command.number} não foi aberta`, { description: "Vá em 'Abertura de Comandas' para liberar esta comanda." });
        return;
      }
      const dest: SelectedDestination = { type: "tab", number: command.number, id: command.id, customerName: command.customer_name || undefined };
      // Comanda é sempre individual — 1 pessoa por comanda, sem prompt
      if (command.status !== "in_use") {
        onSelectDestination(dest, 1);
      } else {
        onSelectDestination(dest);
      }
    }
  }, [selectedType, inputValue, tables, commands, onSelectDestination, requireEntry, hasActiveEntry]);

  const handleTableClick = useCallback((table: { id: string; number: string; status?: string | null }) => {
    setInputValue(table.number);
    // Auto-confirm: simulate pressing ✓
    const normalizedInput = normalizeNumber(table.number);
    const dest: SelectedDestination = { type: "table", number: table.number, id: table.id };
    if (table.status !== "occupied") {
      setPendingDestination(dest);
      setShowPeopleDialog(true);
    } else {
      onSelectDestination(dest);
    }
  }, [onSelectDestination]);

  const handlePeopleConfirm = (peopleCount: number) => {
    if (pendingDestination) {
      onSelectDestination(pendingDestination, peopleCount);
      setPendingDestination(null);
    }
  };

  const occupiedTables = tables.filter(t => t.status === "occupied").map(t => normalizeNumber(t.number));
  const occupiedCommands = usableCommands.filter(c => c.status === "in_use").map(c => normalizeNumber(c.number));

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
      if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) { e.preventDefault(); handleConfirm(); return; }
      if (e.key === 'Escape') { inputValue ? setInputValue('') : onExit?.(); return; }
      if (/^[0-9]$/.test(e.key)) { handleKeyPress(e.key); return; }
      if (e.key === 'Backspace') { e.preventDefault(); handleKeyPress('backspace'); return; }
      if (e.key === 'F1') { e.preventDefault(); setSelectedType('table'); setInputValue(''); }
      else if (e.key === 'F2') { e.preventDefault(); setSelectedType('tab'); setInputValue(''); }
      else if (e.key === 'F3') { e.preventDefault(); setSelectedType('counter'); setInputValue(''); }
      else if (e.key === 'F4') { e.preventDefault(); setSelectedType('delivery'); setInputValue(''); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [inputValue, selectedType, onExit, handleConfirm]);

  const numpadKeys = [
    { label: "7", action: () => handleKeyPress("7") },
    { label: "8", action: () => handleKeyPress("8") },
    { label: "9", action: () => handleKeyPress("9") },
    { label: "⌫", action: () => handleKeyPress("backspace"), variant: "backspace" as const },
    { label: "4", action: () => handleKeyPress("4") },
    { label: "5", action: () => handleKeyPress("5") },
    { label: "6", action: () => handleKeyPress("6") },
    { label: "✕", action: () => handleKeyPress("clear"), variant: "cancel" as const },
    { label: "1", action: () => handleKeyPress("1") },
    { label: "2", action: () => handleKeyPress("2") },
    { label: "3", action: () => handleKeyPress("3") },
    { label: "✓", action: handleConfirm, variant: "confirm" as const },
    { label: "0", action: () => handleKeyPress("0"), span: 4 },
  ];

  const getKeyClass = (variant?: string) => {
    const base = "flex items-center justify-center border font-bold transition-all duration-[120ms] active:scale-[0.94] select-none touch-manipulation cursor-pointer font-['Nunito',sans-serif]";
    switch (variant) {
      case "cancel": return `${base} bg-[#3a0a0a] border-[#7a1a1a] text-[#ff6b6b] text-[22px] hover:bg-[#4a1010] hover:border-[#cc2222]`;
      case "confirm": return `${base} bg-[#0a3d1e] border-[#00c853] text-[#00e676] text-[22px] hover:bg-[#0d4d26] hover:border-[#00e676]`;
      case "backspace": return `${base} bg-[#111829] border-[#1e2d52] text-[#7aabdd] text-[16px] hover:bg-[#142040] hover:border-[#1a5fa0]`;
      default: return `${base} bg-[#111829] border-[#1e2d52] text-[#c8dff5] text-[20px] hover:bg-[#142040] hover:border-[#1a5fa0] hover:text-[#4dd0ff] hover:scale-105`;
    }
  };

  const showNumpad = selectedType === "table" || selectedType === "tab";
  const showMap = selectedType === "table" || selectedType === "tab";
  const today = new Date().toLocaleDateString("pt-BR");

  // Table status helper
  const getTableStatus = (table: { number: string; status?: string | null; grouped_to_order_id?: string | null }) => {
    if (table.grouped_to_order_id) return "grouped";
    const isOccupied = table.status === "occupied";
    const isInactive = isTableInactive(table.number);
    if (isOccupied && isInactive) return "attention";
    if (isOccupied) return "occupied";
    return "available";
  };

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden font-['Nunito',sans-serif]" style={{ background: "#0a0e1a" }}>
      {/* ===== HEADER (64px) ===== */}
      <div
        className="h-[180px] shrink-0 flex items-center px-6"
        style={{ background: "#0d1226", borderBottom: "2px solid #1a3a6e" }}
      >
        {/* LEFT — Brand */}
        <div className="flex items-center gap-6 shrink-0">
          <img src={altusLogoBrand} alt="ALTUS Food" className="h-40 w-auto object-contain" />
          <div className="h-24 w-px" style={{ background: "#1e2d52" }} />
          <div className="flex flex-col leading-tight">
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: "#4dd0ff" }}>Restaurante</span>
            <span className="text-lg font-semibold text-white truncate max-w-[200px]">{restaurantName || "—"}</span>
          </div>
        </div>

        {/* CENTER — Mode tabs */}
        <div className="flex-1 flex items-center justify-center gap-2">
          {modeConfig.map(mode => (
            <button
              key={mode.type}
              className={cn(
                "flex items-center gap-1.5 px-5 py-[9px] text-sm font-bold transition-all font-['Nunito',sans-serif]",
                selectedType === mode.type
                  ? "bg-[#0a3a6e] border-[#0077cc] text-[#4dd0ff]"
                  : "bg-[#111829] border-[#1e2d52] text-[#4a7abf] hover:bg-[#142040] hover:border-[#1a5fa0]"
              )}
              style={{
                borderRadius: 10,
                border: `1px solid ${selectedType === mode.type ? "#0077cc" : "#1e2d52"}`,
                boxShadow: selectedType === mode.type ? "0 0 12px rgba(0,170,255,0.13)" : "none",
              }}
              onClick={() => { setSelectedType(mode.type); setInputValue(""); }}
            >
              {mode.label}
              <span className="text-[10px] opacity-60 hidden lg:inline">({mode.shortcut})</span>
            </button>
          ))}
        </div>

        {/* RIGHT — User info */}
        <div className="flex items-center gap-2 shrink-0">
          {operatorName && (
            <div
              className="flex items-center gap-2 px-3.5 py-[5px]"
              style={{ background: "#111829", border: "1px solid #1e2d52", borderRadius: 20 }}
            >
              <div
                className="flex items-center justify-center font-extrabold"
                style={{ width: 26, height: 26, borderRadius: "50%", background: "#0a3a6e", color: "#4dd0ff", fontSize: 11 }}
              >
                {getInitials(operatorName)}
              </div>
              <span className="text-xs font-bold" style={{ color: "#7aabdd" }}>{operatorName}</span>
            </div>
          )}
          <div
            className="flex items-center gap-1.5 px-2.5 py-1"
            style={
              isConnOnline
                ? { background: "#0a1e10", border: "1px solid #00c853", borderRadius: 20 }
                : { background: "#2a0a0a", border: "1px solid #ff3b3b", borderRadius: 20 }
            }
          >
            <div
              className="rounded-full"
              style={{ width: 7, height: 7, background: isConnOnline ? "#00e676" : "#ff5252" }}
            />
            <span
              className="text-[11px] font-bold"
              style={{ color: isConnOnline ? "#00e676" : "#ff5252" }}
            >
              {isConnOnline ? "Online" : "Offline"}
            </span>
          </div>
        </div>
      </div>

      {/* ===== MAIN BODY ===== */}
      <div className="flex-1 flex min-h-0 overflow-hidden relative" style={{ padding: "20px 24px", gap: 24, alignItems: "flex-start" }}>
        {/* Watermark */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none z-0">
          <img src={altusLogoBrand} alt="" className="w-[70%] max-h-[80%] object-contain opacity-[0.08] scale-[2.5]" draggable={false} />
        </div>
        {/* COLUMN 1 — Flow stepper */}
        <div className="hidden md:flex flex-col items-center gap-1 pt-2 shrink-0 select-none" style={{ minWidth: 140 }}>
          {/* Step 1 — done */}
          <div className="flex items-center gap-2">
            <div
              className="flex items-center justify-center rounded-full text-xs font-bold"
              style={{ width: 28, height: 28, background: "#0a3d1e", border: "2px solid #00c853", color: "#00e676" }}
            >
              ✓
            </div>
            <span className="text-[11px] font-bold line-through" style={{ color: "#2ecc71" }}>Senha digitada</span>
          </div>
          <span className="text-[10px] ml-[29px]" style={{ color: "#1e3050" }}>↓</span>
          {/* Step 2 — current */}
          <div className="flex items-center gap-2">
            <div
              className="flex items-center justify-center rounded-full text-xs font-bold"
              style={{ width: 28, height: 28, background: "#00aaff", color: "#060c18" }}
            >
              2
            </div>
            <span className="text-[11px] font-bold" style={{ color: "#4dd0ff" }}>
              {selectedType === "table" ? "Número da mesa" : selectedType === "tab" ? "Número da comanda" : selectedType === "counter" ? "Balcão" : "Delivery"}
            </span>
          </div>
          <span className="text-[10px] ml-[29px]" style={{ color: "#1e3050" }}>↓</span>
          {/* Step 3 — pending */}
          <div className="flex items-center gap-2">
            <div
              className="flex items-center justify-center rounded-full text-xs font-bold"
              style={{ width: 28, height: 28, background: "#111829", border: "2px solid #1e2d52", color: "#3a5a8c" }}
            >
              3
            </div>
            <span className="text-[11px]" style={{ color: "#3a5a8c" }}>Tela de lançamento</span>
          </div>
        </div>

        {/* COLUMN 2 — Numpad / Counter / Delivery */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedType}
            className="flex flex-col items-center shrink-0"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.2 }}
          >
            {showNumpad && (
              <>
                <p className="text-[10px] font-bold uppercase tracking-[1px] mb-3 text-center" style={{ color: "#3a5a8c" }}>
                  {selectedType === "table" ? "NÚMERO DA MESA" : "NÚMERO DA COMANDA"}
                </p>
                {/* Display field */}
                <div
                  className="flex items-center justify-center"
                  style={{
                    minWidth: 240,
                    minHeight: 64,
                    background: "#060c18",
                    border: "2px solid #1a5fa0",
                    borderRadius: 10,
                    boxShadow: "0 0 16px rgba(0,170,255,0.08)",
                  }}
                >
                  <AnimatePresence mode="popLayout">
                    {inputValue ? (
                      <motion.span
                        key={inputValue}
                        className="font-extrabold font-['Nunito',sans-serif]"
                        style={{ fontSize: 36, color: "#4dd0ff", letterSpacing: 8 }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.1 }}
                      >
                        {inputValue}
                      </motion.span>
                    ) : (
                      <span className="text-sm" style={{ color: "#1e3a5c" }}>—</span>
                    )}
                  </AnimatePresence>
                </div>
                {/* Status hint */}
                <div className="h-5 mb-1 mt-1 text-center">
                  {inputValue && selectedType === "table" && occupiedTables.includes(normalizeNumber(inputValue)) && (
                    <span className="text-xs font-medium" style={{ color: "#ef9f27" }}>Mesa ocupada — continuar pedido existente</span>
                  )}
                  {inputValue && selectedType === "tab" && occupiedCommands.includes(normalizeNumber(inputValue)) && (
                    <span className="text-xs font-medium" style={{ color: "#ef9f27" }}>Comanda ocupada — continuar pedido existente</span>
                  )}
                </div>
                {/* Numpad grid (4 cols, 280px, gap 7) */}
                <div className="grid grid-cols-4" style={{ width: 280, gap: 7 }}>
                  {numpadKeys.map((key, i) => (
                    <motion.button
                      key={i}
                      type="button"
                      className={getKeyClass(key.variant)}
                      style={{
                        height: 58,
                        borderRadius: 9,
                        gridColumn: key.span ? `span ${key.span}` : undefined,
                      }}
                      onClick={key.action}
                      disabled={key.variant === "confirm" && !inputValue}
                      whileTap={{ scale: 0.94 }}
                    >
                      {key.label}
                    </motion.button>
                  ))}
                </div>
                <p className="text-[10px] mt-3 text-center" style={{ color: "#1e3a5c" }}>
                  ✓ confirma · ✕ cancela · ⌫ apaga
                </p>
              </>
            )}

            {selectedType === "counter" && (
              <div className="flex flex-col items-center gap-6 py-8">
                <div className="text-5xl">🍺</div>
                <p className="text-sm font-semibold text-center" style={{ color: "#7aabdd" }}>Pronto para lançar pedidos</p>
                <motion.button
                  className="font-bold transition-all font-['Nunito',sans-serif]"
                  style={{
                    background: "#0a3a6e",
                    border: "2px solid #0077cc",
                    color: "#4dd0ff",
                    borderRadius: 12,
                    padding: "16px 40px",
                    fontSize: 16,
                  }}
                  onClick={handleConfirm}
                  whileTap={{ scale: 0.95 }}
                  whileHover={{ background: "#0d4a8e" }}
                >
                  Abrir Balcão →
                </motion.button>
              </div>
            )}

            {selectedType === "delivery" && (
              <div className="flex flex-col items-center gap-6 py-8">
                <Truck className="h-12 w-12" style={{ color: "#4a7abf", opacity: 0.6 }} />
                <p className="text-sm font-semibold" style={{ color: "#7aabdd" }}>Pedido para entrega</p>
                <p className="text-xs" style={{ color: "#4a7abf" }}>Preencha os dados do cliente</p>
                <motion.button
                  className="font-bold transition-all font-['Nunito',sans-serif]"
                  style={{
                    background: "#0a3a6e",
                    border: "2px solid #0077cc",
                    color: "#4dd0ff",
                    borderRadius: 12,
                    padding: "16px 40px",
                    fontSize: 16,
                  }}
                  onClick={handleConfirm}
                  whileTap={{ scale: 0.95 }}
                >
                  Novo Delivery →
                </motion.button>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* COLUMN 3 — Mesa / Comanda map */}
        {showMap && (
          <div className="flex-1 flex flex-col min-w-0 min-h-0">
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div style={{ width: 3, height: 16, background: "#00aaff", borderRadius: 2 }} />
                <span className="text-[10px] font-bold uppercase tracking-[1px]" style={{ color: "#3a5a8c" }}>
                  {selectedType === "table" ? "MESAS" : "COMANDAS"}
                </span>
              </div>
              {selectedType === "table" && (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <div style={{ width: 10, height: 10, background: "#0a1e10", border: "1px solid #1a4a28", borderRadius: 2 }} />
                    <span className="text-[10px]" style={{ color: "#3a5a8c" }}>Livre</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div style={{ width: 10, height: 10, background: "#0a2a4a", border: "1px solid #1a5fa0", borderRadius: 2 }} />
                    <span className="text-[10px]" style={{ color: "#3a5a8c" }}>Ocupada</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div style={{ width: 10, height: 10, background: "#2a1a0a", border: "1px solid #8a4a0a", borderRadius: 2 }} />
                    <span className="text-[10px]" style={{ color: "#3a5a8c" }}>Atenção</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div style={{ width: 10, height: 10, background: "#1a0a2a", border: "1px solid #7a3aaa", borderRadius: 2 }} />
                    <span className="text-[10px]" style={{ color: "#3a5a8c" }}>Agrupada</span>
                  </div>
                </div>
              )}
            </div>
            {/* Scrollable grid */}
            <div
              className="overflow-y-auto pr-1"
              style={{ maxHeight: 360 }}
            >
              <style>{`
                .pdv-mesa-scroll::-webkit-scrollbar { width: 4px; }
                .pdv-mesa-scroll::-webkit-scrollbar-track { background: transparent; }
                .pdv-mesa-scroll::-webkit-scrollbar-thumb { background: #1e2d52; border-radius: 4px; }
              `}</style>
              {selectedType === "table" && (
                <div className="grid grid-cols-5 gap-1 pdv-mesa-scroll">
                  {tables.map(table => {
                    const status = getTableStatus(table);
                    const consumption = getTableConsumption(table.number);
                    const tileStyle = status === "grouped"
                      ? { background: "#1a0a2a", border: "1px solid #7a3aaa" }
                      : status === "attention"
                      ? { background: "#2a1a0a", border: "1px solid #8a4a0a" }
                      : status === "occupied"
                      ? { background: "#0a2a4a", border: "1px solid #1a5fa0" }
                      : { background: "#0a1e10", border: "1px solid #1a4a28" };
                    const numberColor = status === "grouped" ? "#b07aff" : status === "attention" ? "#ef9f27" : status === "occupied" ? "#4dd0ff" : "#00e676";
                    const statusLabel = status === "grouped" ? "Agrupada" : status === "attention" ? "Espera" : status === "occupied" ? "Aberta" : "Livre";
                    const statusLabelColor = status === "grouped" ? "#6a3aaa" : status === "attention" ? "#8a5a00" : status === "occupied" ? "#1a5fa0" : "#1a6a30";

                    return (
                      <motion.button
                        key={table.id}
                        className="flex flex-col items-center justify-center gap-0.5 cursor-pointer"
                        style={{
                          ...tileStyle,
                          borderRadius: 8,
                          padding: "8px 4px",
                          minHeight: 44,
                        }}
                        onClick={() => handleTableClick(table)}
                        whileHover={{ scale: 1.07 }}
                        whileTap={{ scale: 0.94 }}
                        transition={{ duration: 0.15 }}
                      >
                        <span className="font-extrabold" style={{ fontSize: 16, color: numberColor }}>{table.number}</span>
                        <span className="font-bold uppercase" style={{ fontSize: 8, color: statusLabelColor }}>{statusLabel}</span>
                        {showTableConsumptionValue && status !== "available" && consumption && (
                          <span style={{ fontSize: 8, color: numberColor }}>{formatCurrency(consumption.total)}</span>
                        )}
                      </motion.button>
                    );
                  })}
                </div>
              )}

              {selectedType === "tab" && (
                <div className="grid grid-cols-5 gap-1">
                  {usableCommands.length === 0 && requireEntry ? (
                    <div className="col-span-5 text-center py-6" style={{ color: "#4a7abf" }}>
                      <AlertTriangle className="h-6 w-6 mx-auto mb-2 opacity-50" />
                      <p className="text-xs">Nenhuma comanda aberta.</p>
                    </div>
                  ) : usableCommands.map(command => {
                    const isInUse = command.status === "in_use";
                    const isGrouped = !!command.grouped_to_order_id;
                    const entry = getEntryForCommand(command.number);
                    const cmdBg = isGrouped ? "#1a0a2a" : isInUse ? "#0a2a4a" : "#0a1e10";
                    const cmdBorder = isGrouped ? "#7a3aaa" : isInUse ? "#1a5fa0" : "#1a4a28";
                    const cmdNumColor = isGrouped ? "#b07aff" : isInUse ? "#4dd0ff" : "#00e676";
                    const cmdLabelColor = isGrouped ? "#6a3aaa" : isInUse ? "#1a5fa0" : "#1a6a30";
                    const cmdLabel = isGrouped ? "Agrupada" : isInUse ? "Aberta" : "Livre";
                    return (
                      <motion.button
                        key={command.id}
                        className="flex flex-col items-center justify-center gap-0.5 cursor-pointer"
                        style={{
                          background: cmdBg,
                          border: `1px solid ${cmdBorder}`,
                          borderRadius: 8,
                          padding: "8px 4px",
                          minHeight: 44,
                        }}
                        onClick={() => setInputValue(command.number)}
                        whileHover={{ scale: 1.07 }}
                        whileTap={{ scale: 0.94 }}
                        transition={{ duration: 0.15 }}
                      >
                        <span className="font-extrabold" style={{ fontSize: 16, color: cmdNumColor }}>{command.number}</span>
                        <span className="font-bold uppercase" style={{ fontSize: 8, color: cmdLabelColor }}>
                          {cmdLabel}
                        </span>
                        {command.customer_name && (
                          <span className="truncate max-w-full" style={{ fontSize: 8, color: "#4a7abf" }}>{command.customer_name}</span>
                        )}
                        {entry && !isInUse && (
                          <span className="truncate max-w-full" style={{ fontSize: 8, color: "#00aaff" }}>{entry.entry_type_name}</span>
                        )}
                      </motion.button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Balcão/Delivery: no map */}
        {!showMap && selectedType !== "counter" && selectedType !== "delivery" && null}
      </div>

      {/* Waiter requests alert */}
      {pendingRequests.length > 0 && (
        <div className="border-t border-amber-700/30 bg-amber-900/20 px-4 py-2.5 animate-pulse">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-amber-400" />
              <span className="text-xs font-bold text-amber-300">{pendingRequests.length} solicitação(ões) de garçom</span>
            </div>
            <button className="text-xs text-amber-300 border border-amber-600 rounded px-2 py-1 hover:bg-amber-900/40" onClick={() => setShowWaiterDialog(true)}>
              Ver
            </button>
          </div>
        </div>
      )}

      {/* ===== BOTTOM ACTION BAR ===== */}
      <div
        className="shrink-0 flex items-center overflow-x-auto"
        style={{ background: "#080d18", borderTop: "1px solid #1e2d52", padding: "8px 16px", gap: 6 }}
      >
        {[
          { emoji: "↩", label: "Sair", onClick: onExit },
          { emoji: "🧾", label: "Fechar Contas", onClick: onViewOpenBills },
          { emoji: "👤", label: "Trocar Usuário", onClick: onExit },
          { emoji: "🗺", label: "Mapa de Mesas", onClick: onViewMap, highlight: true },
        ].map((btn, i) => (
          <button
            key={i}
            className="flex flex-col items-center cursor-pointer transition-all font-['Nunito',sans-serif]"
            style={{
              padding: "7px 10px",
              borderRadius: 8,
              minWidth: 60,
              border: `1px solid ${btn.highlight ? "#1a5fa0" : "#1e2d52"}`,
              background: btn.highlight ? "#0a2240" : "#0d1226",
              gap: 3,
            }}
            onClick={btn.onClick}
          >
            <span style={{ fontSize: 18, lineHeight: 1 }}>{btn.emoji}</span>
            <span
              className="font-bold whitespace-nowrap text-center"
              style={{ fontSize: 9, color: btn.highlight ? "#7aabdd" : "#4a7abf" }}
            >
              {btn.label}
            </span>
          </button>
        ))}

        {/* Waiter sessions */}
        <button
          className="flex flex-col items-center cursor-pointer transition-all relative font-['Nunito',sans-serif]"
          style={{
            padding: "7px 10px",
            borderRadius: 8,
            minWidth: 60,
            border: "1px solid #1e2d52",
            background: "#0d1226",
            gap: 3,
          }}
          onClick={() => setShowWaiterDialog(true)}
        >
          <span style={{ fontSize: 18, lineHeight: 1 }}>👨‍🍳</span>
          <span className="font-bold whitespace-nowrap" style={{ fontSize: 9, color: "#4a7abf" }}>Garçons</span>
          {pendingRequests.length > 0 && (
            <Badge variant="destructive" className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[8px]">
              {pendingRequests.length}
            </Badge>
          )}
        </button>

        <div className="flex-1" />

        <button
          className="flex flex-col items-center cursor-pointer transition-all font-['Nunito',sans-serif]"
          style={{
            padding: "7px 10px",
            borderRadius: 8,
            minWidth: 80,
            border: "1px solid #00c853",
            background: "#0a3d1e",
            gap: 3,
          }}
          onClick={() => { setInputValue(""); }}
        >
          <span style={{ fontSize: 18, lineHeight: 1 }}>➕</span>
          <span className="font-bold whitespace-nowrap" style={{ fontSize: 9, color: "#00e676" }}>Nova Venda</span>
        </button>
      </div>

      {/* ===== FOOTER ===== */}
      <div
        className="shrink-0 flex justify-between items-center"
        style={{ background: "#060a14", borderTop: "1px solid #1e2d52", padding: "4px 16px" }}
      >
        <span className="text-[10px]" style={{ color: "#1e3050" }}>
          v2.0.0 · {restaurantName ? `Loja ${restaurantName}` : ""} · {operatorName ? `Usuário: ${operatorName}` : ""} · Dt.mov {today}
        </span>
        <span style={{ fontFamily: "'Exo 2', sans-serif", fontSize: 11, letterSpacing: 1 }}>
          <span style={{ color: "#1a3a60" }}>Powered by </span>
          <span className="font-bold" style={{ color: "#00aaff" }}>ALTUS Food</span>
        </span>
      </div>

      {/* Dialogs */}
      <PeopleCountDialog
        open={showPeopleDialog}
        onOpenChange={setShowPeopleDialog}
        destinationType={pendingDestination?.type === "table" ? "table" : "tab"}
        destinationNumber={pendingDestination?.number || ""}
        onConfirm={handlePeopleConfirm}
      />
      <DeliveryOrderDialog
        open={showDeliveryDialog}
        onOpenChange={setShowDeliveryDialog}
        onConfirm={handleDeliveryConfirm}
      />
      {showWaiterDialog && (
        <WaiterSessionsDialogControlled open={showWaiterDialog} onOpenChange={setShowWaiterDialog} />
      )}
    </div>
  );
}

// Controlled waiter sessions dialog
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { WaiterSessionsCard } from "./WaiterSessionsCard";

function WaiterSessionsDialogControlled({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Gerenciar Garçons</DialogTitle>
        </DialogHeader>
        <WaiterSessionsCard />
      </DialogContent>
    </Dialog>
  );
}
